import React, { useState, useRef } from "react";
import axios from "axios";
import { Search, Music, Loader2, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { PageShell, PillarSection } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCIES = [
  { hz: "174 Hz", labelFr: "Sécurité & Apaisement des douleurs", labelEn: "Safety & Pain relief",
    descFr: "Sécurité et apaisement des douleurs physiques.",
    descEn: "Safety and relief of physical pain." },
  { hz: "285 Hz", labelFr: "Régénération des tissus & Vitalité", labelEn: "Tissue regeneration & Vitality",
    descFr: "Régénération des tissus et vitalité.",
    descEn: "Tissue regeneration and vitality." },
  { hz: "396 Hz", labelFr: "Libération des peurs", labelEn: "Release of fears",
    descFr: "Libération des peurs et des blocages.",
    descEn: "Release of fears and blockages." },
  { hz: "417 Hz", labelFr: "Nettoyage des mémoires des murs", labelEn: "Cleansing of wall memories",
    descFr: "Nettoyage des mémoires des murs et énergies passées.",
    descEn: "Cleansing of wall memories and past energies." },
  { hz: "432 Hz", labelFr: "Fréquence de Référence · Nature", labelEn: "Reference frequency · Nature",
    descFr: "Fréquence de Référence (Nature) — accordage au vivant.",
    descEn: "Reference frequency (Nature) — attunement to the living." },
  { hz: "528 Hz", labelFr: "Fréquence Miracle · Réparation ADN", labelEn: "Miracle frequency · DNA repair",
    descFr: "Fréquence Miracle — réparation de l'ADN et bio-résonance.",
    descEn: "Miracle frequency — DNA repair and bio-resonance." },
  { hz: "639 Hz", labelFr: "Harmonie relationnelle du foyer", labelEn: "Relational harmony of the home",
    descFr: "Harmonie relationnelle et communication du foyer.",
    descEn: "Relational harmony and communication within the home." },
  { hz: "741 Hz", labelFr: "Détoxication cellulaire", labelEn: "Cellular detox",
    descFr: "Détoxication cellulaire (pollutions électromagnétiques).",
    descEn: "Cellular detox (electromagnetic pollutions)." },
  { hz: "7.83 Hz", labelFr: "Résonance de Schumann · Ancrage", labelEn: "Schumann resonance · Grounding",
    descFr: "Résonance de Schumann — ancrage profond et lutte contre la fatigue.",
    descEn: "Schumann resonance — deep grounding and fatigue relief." },
  { hz: "852 Hz", labelFr: "Éveil spirituel & Intuition", labelEn: "Spiritual awakening & Intuition",
    descFr: "Éveil spirituel et intuition.",
    descEn: "Spiritual awakening and intuition." },
  { hz: "963 Hz", labelFr: "Fréquence de Tesla · Unité", labelEn: "Tesla frequency · Unity",
    descFr: "Fréquence de Tesla — reconnexion à l'Unité.",
    descEn: "Tesla frequency — reconnection to Unity." },
];

// Fiches pédagogiques approfondies — 6 fréquences signature FACOW360
const FREQ_DETAILS = [
  {
    hz: "174 Hz",
    titleFr: "Le Fondement de la Sécurité",
    titleEn: "The Foundation of Safety",
    actionFr: "Agit comme un anesthésique naturel. Elle apporte un sentiment de sécurité et de sérénité.",
    actionEn: "Acts as a natural anaesthetic. It brings a feeling of safety and serenity.",
    usageFr: "Idéale pour apaiser les tensions physiques et se sentir « chez soi » après un grand changement.",
    usageEn: 'Ideal to soothe physical tensions and feel "at home" after a major change.',
  },
  {
    hz: "285 Hz",
    titleFr: "Le Souffle de Vitalité",
    titleEn: "The Breath of Vitality",
    actionFr: "Favorise la régénération des tissus et redynamise le champ énergétique.",
    actionEn: "Promotes tissue regeneration and reenergises the energetic field.",
    usageFr: "À utiliser pour booster son énergie le matin ou après une période de fatigue.",
    usageEn: "Use to boost energy in the morning or after a period of fatigue.",
  },
  {
    hz: "432 Hz",
    titleFr: "Le Diapason de la Nature",
    titleEn: "The Diapason of Nature",
    actionFr: "C'est la fréquence de résonance du vivant et de l'eau. Elle harmonise l'habitat avec les rythmes biologiques de la Terre.",
    actionEn: "The resonance frequency of the living and of water. It harmonises the home with the Earth's biological rhythms.",
    usageFr: "À diffuser en fond sonore permanent pour une atmosphère apaisée et naturelle.",
    usageEn: "Play as continuous background for a peaceful, natural atmosphere.",
  },
  {
    hz: "528 Hz",
    titleFr: "La Fréquence Miracle",
    titleEn: "The Miracle Frequency",
    actionFr: "Reconnue pour son influence sur la réparation de l'ADN et le retour à l'équilibre cellulaire.",
    actionEn: "Renowned for its influence on DNA repair and the return to cellular balance.",
    usageFr: "Pour une séance de profonde transformation ou de bio-résonance.",
    usageEn: "For a deep transformation or bio-resonance session.",
  },
  {
    hz: "7.83 Hz",
    titleFr: "Le Battement de Cœur de la Terre · Schumann",
    titleEn: "The Earth's Heartbeat · Schumann",
    actionFr: "Assure un ancrage profond. Elle contrebalance le stress lié aux ondes artificielles et à la vie urbaine.",
    actionEn: "Provides deep grounding. It counterbalances the stress linked to artificial waves and urban life.",
    usageFr: "Indispensable pour retrouver son centre et lutter contre la dispersion mentale.",
    usageEn: "Essential to find your centre again and counter mental dispersion.",
  },
  {
    hz: "963 Hz",
    titleFr: "La Fréquence de Tesla · Le 9",
    titleEn: "The Tesla Frequency · The 9",
    actionFr: "Éveil de la conscience et reconnexion à l'Unité.",
    actionEn: "Awakening of consciousness and reconnection to Unity.",
    usageFr: "Pour les moments de méditation profonde et de clarté spirituelle.",
    usageEn: "For moments of deep meditation and spiritual clarity.",
  },
];

export default function Frequences() {
  const { locale, t } = useLang();
  const isFr = locale === "fr";

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // {video_id, title, channel}
  const inputRef = useRef(null);

  const runSearch = async (q) => {
    const term = (q ?? query).trim();
    if (!term) return;
    setLoading(true);
    setError("");
    setSelected(null);
    try {
      const r = await axios.get(`${API}/youtube/search`, { params: { q: term, max_results: 5 } });
      setResults(r.data.results || []);
      if (!r.data.results?.length) {
        setError(isFr ? "Aucun résultat trouvé pour cette recherche." : "No results for this search.");
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 503) {
        setError(isFr
          ? "Le moteur de recherche musical est en cours de finalisation. Disponible très bientôt."
          : "The musical search engine is being finalised. Available very soon.");
      } else if (status === 429) {
        setError(isFr
          ? "Le quota de recherche journalier est atteint. Merci de réessayer demain."
          : "Daily search quota reached. Please try again tomorrow.");
      } else {
        setError(isFr
          ? "Impossible de lancer la recherche pour le moment."
          : "Unable to run the search right now.");
      }
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const onSuggestionClick = (freq) => {
    const term = `${freq.hz} ${isFr ? freq.labelFr : freq.labelEn}`;
    setQuery(term);
    runSearch(term);
  };

  const clearAll = () => {
    setQuery("");
    setResults([]);
    setSelected(null);
    setError("");
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <PageShell
      testId="page-frequences"
      title={isFr ? "Fréquences musicales" : "Musical Frequencies"}
      subtitle={isFr
        ? "Un espace libre pour choisir vous-même la fréquence qui vous appelle. La musique ne démarre jamais automatiquement — vous êtes seul·e maître du Play."
        : "A free space to choose for yourself the frequency that calls you. Music never starts automatically — you alone press Play."}
    >
      <Reveal direction="up" amount={0.2}>
        <PillarSection
          heading={isFr ? "Pourquoi la ré-information vibratoire ?" : "Why vibrational re-information?"}
          testId="freq-section-intro"
        >
          <p>
            {isFr
              ? "Une intervention en géobiologie libère votre espace des pollutions invisibles. C'est une détox de l'habitat. Mais une fois le vide créé, il est crucial de le remplir par une information positive. C'est tout l'objet du protocole FACOW360 : utiliser les fréquences sacrées comme un engrais vibratoire pour stabiliser votre équilibre et celui de votre foyer."
              : "A geobiology intervention frees your space from invisible pollutions. It is a habitat detox. But once the emptiness is created, it is crucial to fill it with positive information. This is the very purpose of the FACOW360 protocol: using sacred frequencies as a vibrational fertiliser to stabilise your balance and that of your home."}
          </p>
          <p>
            {isFr
              ? "Supprimer les ondes nocives est une étape ; nourrir l'habitat avec les bonnes fréquences en est le complément indispensable. Cette page vous laisse libre du choix : tapez ce qui vous appelle, ou cliquez l'une des cartes ci-dessous. Aucune musique ne démarre d'elle-même — vous restez seul·e maître du Play."
              : "Removing harmful waves is one step; nourishing the home with the right frequencies is its indispensable complement. This page leaves you free to choose: type what calls you, or click one of the cards below. No music ever starts on its own — you alone press Play."}
          </p>
        </PillarSection>
      </Reveal>

      {/* Barre de recherche */}
      <div className="mt-10" data-testid="freq-search-block">
        <div className="relative">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#00F0FF]"
          />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder={isFr
              ? "ex : 432 Hz, musique relaxante, ondes alpha…"
              : "e.g.: 432 Hz, relaxing music, alpha waves…"}
            className="w-full pl-12 pr-32 py-4 bg-[#0A1128]/70 border border-[#D4AF37]/30 rounded-2xl text-slate-100 placeholder:text-slate-500 font-mono-tech text-sm tracking-wide focus:outline-none focus:border-[#00F0FF]/60 focus:ring-1 focus:ring-[#00F0FF]/40 transition-colors"
            data-testid="freq-search-input"
          />
          <button
            onClick={() => runSearch()}
            disabled={loading || !query.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 btn-primary !px-4 !py-2 !text-xs"
            data-testid="freq-search-submit"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : (isFr ? "Rechercher" : "Search")}
          </button>
        </div>

        {/* Cartes de fréquences (pédagogiques) */}
        <div className="mt-8">
          <p className="label-overline mb-4">
            {isFr ? "— Les 11 fréquences sacrées" : "— The 11 sacred frequencies"}
          </p>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="freq-suggestions"
          >
            {FREQUENCIES.map((f, i) => (
              <Reveal key={f.hz} direction="up" delay={i * 0.04} duration={0.7} amount={0.1}>
                <button
                  onClick={() => onSuggestionClick(f)}
                  className="group relative w-full text-left rounded-2xl border border-[#D4AF37]/25 bg-[#0A1128]/60 hover:bg-[#0A1128] hover:border-[#D4AF37]/70 transition-all p-5 h-full"
                  data-testid={`freq-suggestion-${f.hz.replace(/\s+/g, "").replace(".", "_")}`}
                >
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="font-display text-2xl md:text-3xl text-amber-glow leading-none">
                      {f.hz}
                    </span>
                    <span className="text-[10px] font-mono-tech uppercase tracking-[0.22em] text-[#00F0FF]/80 group-hover:text-[#00F0FF] transition-colors">
                      {isFr ? f.labelFr : f.labelEn}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    {isFr ? f.descFr : f.descEn}
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500 group-hover:text-[#D4AF37] transition-colors">
                    {isFr ? "Écouter" : "Listen"} →
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>

        {error && (
          <p
            className="mt-6 text-sm text-amber-glow/90 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl px-4 py-3"
            data-testid="freq-error"
          >
            {error}
          </p>
        )}
      </div>

      {/* Lecteur sélectionné (manuel) */}
      {selected && (
        <div className="mt-10" data-testid="freq-player-block">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <p className="label-overline mb-1">{isFr ? "— Lecteur" : "— Player"}</p>
              <h3 className="font-display text-lg md:text-xl text-amber-glow leading-tight">
                {selected.title}
              </h3>
              {selected.channel && (
                <p className="text-xs text-slate-500 font-mono-tech mt-1">{selected.channel}</p>
              )}
            </div>
            <button
              onClick={() => setSelected(null)}
              className="text-slate-400 hover:text-white shrink-0 mt-1"
              data-testid="freq-player-close"
              aria-label="Close player"
            >
              <X size={18} />
            </button>
          </div>
          <div className="aspect-video w-full rounded-2xl overflow-hidden border border-[#00F0FF]/30 bg-black">
            <iframe
              key={selected.video_id}
              src={`https://www.youtube-nocookie.com/embed/${selected.video_id}?autoplay=0&rel=0&modestbranding=1`}
              title={selected.title}
              loading="lazy"
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              data-testid="freq-player-iframe"
            />
          </div>
          <p className="mt-2 text-[11px] text-slate-500 italic">
            {isFr
              ? "▶ Lancement manuel. Cliquez sur la touche Play du lecteur pour démarrer l'écoute."
              : "▶ Manual start. Click the player's Play button to begin listening."}
          </p>
        </div>
      )}

      {/* Résultats */}
      {results.length > 0 && (
        <div className="mt-10" data-testid="freq-results-block">
          <div className="flex items-center justify-between mb-4">
            <p className="label-overline">
              {isFr ? `— ${results.length} résultats` : `— ${results.length} results`}
            </p>
            <button
              onClick={clearAll}
              className="text-[11px] uppercase tracking-[0.2em] font-mono-tech text-slate-400 hover:text-[#00F0FF] transition-colors flex items-center gap-1.5"
              data-testid="freq-clear-btn"
            >
              <X size={12} /> {isFr ? "Effacer" : "Clear"}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((r) => {
              const isActive = selected?.video_id === r.video_id;
              return (
                <button
                  key={r.video_id}
                  onClick={() => setSelected(r)}
                  className={`group text-left rounded-2xl overflow-hidden border bg-[#0F1A3A] transition-all ${
                    isActive
                      ? "border-[#00F0FF]/70 ring-1 ring-[#00F0FF]/40"
                      : "border-[#D4AF37]/20 hover:border-[#D4AF37]/60"
                  }`}
                  data-testid={`freq-result-${r.video_id}`}
                >
                  <div className="relative aspect-video overflow-hidden">
                    {r.thumbnail ? (
                      <img
                        src={r.thumbnail}
                        alt={r.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#0A1128]">
                        <Music size={32} className="text-slate-600" />
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F1A3A]/85 via-transparent to-transparent" />
                  </div>
                  <div className="p-4">
                    <h4 className="font-display text-sm text-amber-glow leading-snug line-clamp-2">
                      {r.title}
                    </h4>
                    {r.channel && (
                      <p className="mt-1 text-[11px] text-slate-500 font-mono-tech truncate">
                        {r.channel}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ============================ FRÉQUENCES EN DÉTAIL (Accordéon) ============================ */}
      <div className="mt-16" data-testid="freq-details-block">
        <Reveal direction="up" amount={0.15}>
          <p className="label-overline mb-3">
            {isFr ? "— Pour aller plus loin" : "— Going deeper"}
          </p>
          <h2 className="font-display text-2xl md:text-4xl text-amber-glow leading-tight tracking-tight mb-4">
            {isFr ? "Les fréquences en détail" : "Frequencies in detail"}
          </h2>
          <p className="text-slate-300 leading-relaxed max-w-3xl mb-8 text-base">
            {isFr
              ? "Six fréquences signature, expliquées sans détour : leur action sur le vivant, et la juste manière de les utiliser dans votre quotidien."
              : "Six signature frequencies, explained without detour: their action on the living, and the right way to use them in your daily life."}
          </p>
        </Reveal>

        <div className="space-y-3">
          {FREQ_DETAILS.map((d, i) => (
            <Reveal key={d.hz} direction="up" delay={i * 0.05} duration={0.7} amount={0.1}>
              <details
                className="group rounded-2xl border border-[#D4AF37]/25 bg-[#0A1128]/60 open:bg-[#0A1128] open:border-[#D4AF37]/60 transition-colors"
                data-testid={`freq-detail-${d.hz.replace(/\s+/g, "").replace(".", "_")}`}
              >
                <summary className="list-none cursor-pointer px-5 py-4 flex items-center justify-between gap-4">
                  <div className="flex items-baseline gap-4 min-w-0">
                    <span className="font-display text-2xl md:text-3xl text-amber-glow leading-none shrink-0">
                      {d.hz}
                    </span>
                    <span className="text-sm md:text-base text-slate-200 font-display tracking-tight truncate">
                      — {isFr ? d.titleFr : d.titleEn}
                    </span>
                  </div>
                  <span
                    className="shrink-0 text-[#00F0FF] text-2xl leading-none font-mono-tech transition-transform group-open:rotate-45"
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <div className="px-5 pb-5 pt-1 grid md:grid-cols-2 gap-5 border-t border-[#D4AF37]/15 mt-1">
                  <div>
                    <p className="label-overline mb-2 text-[#00F0FF]">
                      {isFr ? "— Action" : "— Action"}
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {isFr ? d.actionFr : d.actionEn}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline mb-2 text-[#D4AF37]">
                      {isFr ? "— Usage" : "— Usage"}
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {isFr ? d.usageFr : d.usageEn}
                    </p>
                  </div>
                </div>
              </details>
            </Reveal>
          ))}
        </div>
      </div>

      {/* ============================ SIGNATURE FABRICE ============================ */}
      <Reveal direction="up" amount={0.2}>
        <blockquote
          className="mt-16 relative rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 via-[#0A1128]/60 to-[#00F0FF]/5 px-8 py-10 md:px-12 md:py-14"
          data-testid="freq-signature"
        >
          <span
            className="absolute top-4 left-6 font-display text-7xl text-[#D4AF37]/40 leading-none select-none"
            aria-hidden="true"
          >
            “
          </span>
          <p className="relative font-display text-xl md:text-2xl lg:text-3xl text-slate-100 leading-snug tracking-tight max-w-3xl">
            {isFr
              ? "Mon expertise ne se contente pas de supprimer l'encombrement invisible ; elle installe une nouvelle harmonie. C'est le passage de la neutralité à la plénitude."
              : "My expertise does not merely remove invisible clutter; it installs a new harmony. It is the passage from neutrality to plenitude."}
          </p>
          <footer className="mt-6 text-[11px] font-mono-tech uppercase tracking-[0.3em] text-[#D4AF37]">
            — Fabrice Marini · FACOW360
          </footer>
        </blockquote>
      </Reveal>

      <div
        className="mt-12 rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-6 py-5"
        data-testid="freq-responsibility"
      >
        <p className="label-overline mb-2 text-[#D4AF37]">
          {isFr ? "— Clause de responsabilité" : "— Disclaimer"}
        </p>
        <p className="text-sm text-slate-300 leading-relaxed italic">
          {isFr
            ? "En tant que conseiller, mon accompagnement vibratoire repose sur une expertise technique et sensitive. Il ne constitue en aucun cas un avis médical ou un diagnostic de santé. Mes services visent l'équilibre de l'habitat et le bien-être général."
            : "As a consultant, my vibrational accompaniment rests on technical and sensitive expertise. It does not constitute medical advice or a health diagnosis under any circumstances. My services aim at habitat balance and general well-being."}
        </p>
      </div>

      <p className="mt-6 text-xs text-slate-500 italic" data-testid="freq-disclaimer">
        {isFr
          ? "FACOW360 ne diffuse aucune musique automatiquement. Les vidéos affichées proviennent de YouTube et restent soumises à ses conditions. Le visiteur est seul maître du lancement de la lecture."
          : "FACOW360 streams no music automatically. The videos displayed are hosted on YouTube and remain subject to its terms. The visitor alone controls playback."}
      </p>
    </PageShell>
  );
}
